import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import ProgressBar from 'react-bootstrap/ProgressBar'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/Stack'

import { formatCurrency } from '../../utilities/helpers'
import FundraisingLandingDonation from './FundraisingLandingDonation'
import './fundraiserCampaignWrapper.scss'

const getProgressPercent = (raisedAmount, goalAmount) => {
  const raised = Number(raisedAmount || 0)
  const goal = Number(goalAmount || 0)
  if (goal <= 0) return 0
  return Math.min(100, Math.round((raised / goal) * 100))
}

const formatDate = (value) => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function FundraiserProgress({ campaign, progress, accentColor }) {
  if (!campaign.showGoalProgress || campaign.goalAmount <= 0) return null

  return (
    <div className="mb-3">
      <div className="fundraiser-progress-raised mb-1">
        {formatCurrency(campaign.raisedAmount)} raised
      </div>
      <div className="fundraiser-progress-goal mb-3">
        of {formatCurrency(campaign.goalAmount)} goal
      </div>
      <ProgressBar
        now={progress}
        className="fundraiser-progress-bar"
        style={{ '--fundraiser-accent': accentColor }}
      />
      <div className="small text-muted mt-2">{progress}% complete</div>
    </div>
  )
}

function FundraiserDonateSidebar({
  campaign,
  organizationUUID,
  organizationSlug,
  participantUuid,
  accentColor,
  donateHref,
}) {
  return (
    <Card className="fundraiser-donate-card border-0 mb-4 mb-lg-0">
      <Card.Body className="p-4">
        <FundraiserProgress
          campaign={campaign}
          progress={getProgressPercent(
            campaign.raisedAmount,
            campaign.goalAmount
          )}
          accentColor={accentColor}
        />

        {campaign.enableLandingPageDonation ? (
          <FundraisingLandingDonation
            campaign={campaign}
            organizationUUID={organizationUUID || campaign.organizationUUID}
            organizationSlug={organizationSlug}
            initialParticipantUuid={participantUuid}
            embedded
            sidebar
          />
        ) : (
          <Stack gap={3}>
            {donateHref ? (
              <Button
                as={Link}
                to={donateHref}
                onClick={() =>
                  sessionStorage.removeItem('fundraisingParticipantUuid')
                }
                className="fundraiser-donate-btn"
                style={{
                  backgroundColor: accentColor,
                  borderColor: accentColor,
                }}
              >
                {campaign.ctaButton || 'Donate now'}
              </Button>
            ) : (
              <Button
                disabled
                className="fundraiser-donate-btn"
                style={{
                  backgroundColor: accentColor,
                  borderColor: accentColor,
                }}
              >
                {campaign.ctaButton || 'Donate now'}
              </Button>
            )}

            {!campaign.ticketPurchaseUrl && (
              <p className="small text-muted mb-0">
                Donations are added during ticket checkout when this campaign is
                linked to an event or package.
              </p>
            )}

            {campaign.suggestedAmounts?.length > 0 && (
              <div>
                <div className="small fw-semibold mb-2">Suggested amounts</div>
                <div className="fundraiser-amount-grid">
                  {campaign.suggestedAmounts.map((amount) => (
                    <Badge
                      key={amount}
                      bg="light"
                      text="dark"
                      className="py-2 px-3 text-center"
                    >
                      {amount}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </Stack>
        )}

        {campaign.ticketPurchaseUrl && (
          <Button
            as={Link}
            to={campaign.ticketPurchaseUrl}
            variant="outline-secondary"
            className="w-100 mt-3"
          >
            {campaign.secondaryButton || 'Buy Tickets'}
          </Button>
        )}
      </Card.Body>
    </Card>
  )
}

export default function FundraisingCampaignWrapper({
  campaign,
  organizationUUID,
  organizationSlug,
}) {
  const [participantUuid, setParticipantUuid] = useState('')
  const progress = getProgressPercent(
    campaign?.raisedAmount,
    campaign?.goalAmount
  )
  const accentColor = campaign?.primaryColor || '#0057b8'

  const donateHref = campaign?.ticketPurchaseUrl || null

  useEffect(() => {
    const sessionParticipantUuid = sessionStorage.getItem(
      'fundraisingParticipantUuid'
    )
    setParticipantUuid(sessionParticipantUuid || '')
  }, [campaign?.slug])

  const storeParticipant = (nextParticipantUuid) => {
    if (nextParticipantUuid) {
      sessionStorage.setItem('fundraisingParticipantUuid', nextParticipantUuid)
      setParticipantUuid(nextParticipantUuid)
    }
  }

  return (
    <div className="fundraiser-page">
      {campaign.organizationLogoUrl && (
        <div className="fundraiser-org-logo">
          <img
            src={campaign.organizationLogoUrl}
            alt={campaign.organizationName || 'Organization logo'}
            width={105}
            height={50}
          />
        </div>
      )}

      <Row className="g-4">
        <Col lg={7} className="order-lg-1 order-2">
          {campaign.bannerImageUrl && (
            <div className="fundraiser-hero-image mb-4">
              <img
                src={campaign.bannerImageUrl}
                alt={`${campaign.title} banner`}
              />
            </div>
          )}

          <div className="mb-4">
            <Badge bg="light" text="dark" className="mb-3">
              Fundraiser
            </Badge>
            <h1 className="mb-3">{campaign.title}</h1>

            {(campaign.startDate || campaign.endDate) && (
              <p className="small text-muted mb-3">
                {campaign.startDate ? formatDate(campaign.startDate) : 'Now'}
                {campaign.endDate ? ` – ${formatDate(campaign.endDate)}` : ''}
              </p>
            )}

            {campaign.description && (
              <div className="fundraiser-story">{campaign.description}</div>
            )}
          </div>

          {campaign.showRecentDonors && campaign.recentDonors?.length > 0 && (
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <h2 className="h5 mb-4">Donations</h2>
                <Stack gap={0}>
                  {campaign.recentDonors.map((donor, index) => (
                    <div
                      key={`${donor.name}-${donor.occurredAt}-${index}`}
                      className="fundraiser-donor-item"
                    >
                      <Stack
                        direction="horizontal"
                        className="justify-content-between align-items-start"
                      >
                        <div>
                          <div className="fw-semibold">{donor.name}</div>
                          {donor.message && (
                            <div className="small text-muted mt-1">
                              &ldquo;{donor.message}&rdquo;
                            </div>
                          )}
                          {donor.occurredAt && (
                            <div className="small text-muted mt-1">
                              {formatDate(donor.occurredAt)}
                            </div>
                          )}
                        </div>
                        <span className="fw-semibold ms-3">
                          {formatCurrency(donor.amount)}
                        </span>
                      </Stack>
                    </div>
                  ))}
                </Stack>
              </Card.Body>
            </Card>
          )}

          {campaign.participants?.length > 0 && (
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <h2 className="h5 mb-4">Participants</h2>
                <Row className="g-3">
                  {campaign.participants.map((participant) => (
                    <Col md={6} key={participant.uuid}>
                      <Card className="h-100 border">
                        <Card.Body>
                          <div className="fw-semibold">{participant.name}</div>
                          {participant.goalAmount > 0 && (
                            <>
                              <div className="small text-muted mt-1">
                                {formatCurrency(participant.raisedAmount)} of{' '}
                                {formatCurrency(participant.goalAmount)}
                              </div>
                              <ProgressBar
                                className="mt-2"
                                now={getProgressPercent(
                                  participant.raisedAmount,
                                  participant.goalAmount
                                )}
                                style={{ height: '6px' }}
                              />
                            </>
                          )}
                          {donateHref && (
                            <Button
                              as={Link}
                              to={donateHref}
                              onClick={() => storeParticipant(participant.uuid)}
                              size="sm"
                              variant="outline-primary"
                              className="mt-3"
                            >
                              Support {participant.name}
                            </Button>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          )}

          {campaign.showLeaderboard && campaign.leaderboard?.length > 0 && (
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <h2 className="h5 mb-4">Leaderboard</h2>
                <Stack gap={2}>
                  {campaign.leaderboard.map((entry, index) => (
                    <Stack
                      key={entry.uuid}
                      direction="horizontal"
                      className="justify-content-between border-bottom pb-2"
                    >
                      <span>
                        <span className="text-muted me-2">#{index + 1}</span>
                        {entry.name}
                      </span>
                      <span className="fw-semibold">
                        {formatCurrency(entry.raisedAmount)}
                      </span>
                    </Stack>
                  ))}
                </Stack>
              </Card.Body>
            </Card>
          )}

          {campaign.showSponsorLogos && campaign.sponsors?.length > 0 && (
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <h2 className="h5 mb-4">Sponsors</h2>
                <Row className="g-3">
                  {campaign.sponsors.map((sponsor) => (
                    <Col md={6} key={sponsor.uuid}>
                      <Card className="h-100 border">
                        <Card.Body>
                          {sponsor.logoUrl && (
                            <img
                              src={sponsor.logoUrl}
                              alt={sponsor.companyName}
                              className="mb-2"
                              style={{ maxHeight: '48px', maxWidth: '160px' }}
                            />
                          )}
                          <div className="fw-semibold">
                            {sponsor.website ? (
                              <a
                                href={sponsor.website}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {sponsor.companyName}
                              </a>
                            ) : (
                              sponsor.companyName
                            )}
                          </div>
                          {sponsor.businessOffer && (
                            <div className="small text-muted mt-1">
                              {sponsor.businessOffer}
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col lg={5} className="order-lg-2 order-1">
          <div className="fundraiser-sidebar">
            <FundraiserDonateSidebar
              campaign={campaign}
              organizationUUID={organizationUUID}
              organizationSlug={organizationSlug}
              participantUuid={participantUuid}
              accentColor={accentColor}
              donateHref={donateHref}
            />
          </div>
        </Col>
      </Row>
    </div>
  )
}
